import { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import GreenButton from '../../common/styled/GreenButton/GreenButton';
import CustomLoader from '../../components/CustomLoader/CustomLoader';
import LanguageDetailsDialog from '../../dialogs/LanguageDetailsDialog/LanguageDetailsDialog';
import Language from '../../types/Language';
import api from '../../utils/api';
import { useDialog } from '../../utils/dialog';
import snackbar from '../../utils/snackbar';
import TranslationProgress from './components/TranslationProgress/TranslationProgress';
import { ActionTableCell, FlagTableCell, FlexContainer, StyledButton, StyledFlag, Title } from './styled';

const LanguagesPage = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const [openDialog, closeDialog] = useDialog();

    const [isLoading, setIsLoading] = useState(true);
    const [languages, setLanguages] = useState<Language[]>([]);
    const [selectedLanguageId, selectLanguageId] = useState(0);

    const [isLanguageDialogOpen, setLanguageDialogOpen] = useState(false);

    const getData = useCallback(() => {
        api.request('/languages').then((res) => {
            setLanguages(res);
            setIsLoading(false);
        });
    }, []);

    useEffect(() => {
        if (!isLanguageDialogOpen) getData();
    }, [getData, isLanguageDialogOpen]);

    const openLanguageDialog = (languageId: number) => {
        selectLanguageId(languageId);
        setLanguageDialogOpen(true);
    };

    const closeLanguageDialog = useCallback((languageId?: number) => {
        if (languageId) {
            selectLanguageId(languageId);
        }
        setLanguageDialogOpen(false);
    }, [setLanguageDialogOpen]);

    const deleteLanguage = useCallback((languageId: number) => {
        api.request('/languages/' + languageId, 'DELETE').then(() => {
            snackbar.success(t('dialogs.language_delete.success'));
            getData();
        });
    }, [getData, t]);

    const deleteLanguageConfirm = useCallback((language: Language) => {
        const code = language.code;
        openDialog({
            title: t('dialogs.language_delete.title'),
            message: <Trans i18nKey='dialogs.language_delete.message'>_<strong>{{code}}</strong>_</Trans>,
            actions: [
                {
                    text: t('dialogs.generic.cancel'),
                    color: 'secondary',
                    action: closeDialog
                },
                {
                    text: t('dialogs.generic.confirm'),
                    color: 'primary',
                    action: () => {
                        deleteLanguage(language.id);
                        closeDialog();
                    }
                }
            ]
        });
    }, [closeDialog, deleteLanguage, openDialog, t]);

    if (isLoading) {
        return <CustomLoader />;
    }

    return (
        <>
            <FlexContainer>
                <Title variant='h4'>{t('pages.languages.title')}</Title>
                {<GreenButton variant='outlined' onClick={() => openLanguageDialog(0)}>
                    {t('pages.languages.new_language')}
                </GreenButton>}
            </FlexContainer>
            <TableContainer component={Paper} style={{marginTop: '20px'}} className='book-list-table-view'>
                <Table aria-label='simple table'>
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell>{t('pages.languages.table.language')}</TableCell>
                            <TableCell>{t('pages.languages.table.code')}</TableCell>
                            <TableCell>{t('pages.languages.table.progress')}</TableCell>
                            <TableCell>{t('pages.languages.table.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {languages.map(language => {
                            const countryCode = language.code.split('-').pop();

                            return (
                                <TableRow key={language.id}>
                                    <FlagTableCell>
                                        <StyledFlag code={countryCode} />
                                    </FlagTableCell>
                                    <TableCell>{language.description}</TableCell>
                                    <TableCell>{language.code}</TableCell>
                                    <TableCell>
                                        <TranslationProgress value={language.progress} />
                                    </TableCell>
                                    <ActionTableCell>
                                        {language.code !== 'it-IT' && (
                                            <>
                                                <StyledButton variant='outlined' color='primary' onClick={() => openLanguageDialog(language.id)}>
                                                    {t('pages.languages.table.details')}
                                                </StyledButton>
                                                <StyledButton variant='outlined' color='primary' onClick={() => history.push('/languages/' + language.id)}>
                                                    {t('pages.languages.table.translation')}
                                                </StyledButton>
                                                <StyledButton variant='outlined' color='secondary' onClick={() => deleteLanguageConfirm(language)}>
                                                    {t('pages.languages.table.delete')}
                                                </StyledButton>
                                            </>
                                        )}

                                    </ActionTableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <LanguageDetailsDialog open={isLanguageDialogOpen} onClose={closeLanguageDialog} languageId={selectedLanguageId} />
        </>
    );
};

export default LanguagesPage;
